import React, { useEffect, useCallback, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import { List, Map, fromJS } from 'immutable'
import _get from 'lodash/get'
import { useLazyQuery } from '@apollo/client'
import { getBoundActions } from '../../../actions/index'
import { getPrimary } from '../../../services/languages/index'
import { GET_COURSE_MODULES } from '../../../services/detail/query'
import { CONTENT_IMAGE_LIST } from '../../../services/content/index'
import { getFeaturedEpisode } from '../../../services/tiles/index'
import { APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK } from '../../../services/apollo-graphql/index'
import Sherpa from '../../Sherpa/index'
import { getCourseTabsData, getCourseFeaturedContent } from '../utils'
import DetailPageV3Title from '../DetailPageV3Title'
import DetailPageV3ActionBar from '../DetailPageV3ActionBar'
import DetailPageV3InfoMetaItems from '../DetailPageV3InfoMetaItems'
import DetailPageV3MetaItem from '../DetailPageV3MetaItem'
import DetailPageV3ActionButtons from '../DetailPageV3ActionButtons/DetailPageV3ActionButtons'
import DetailPageV3ActionCopyLink from '../DetailPageV3ActionButtons/DetailPageV3ActionCopyLink'
import DetailPageV3ActionVote from '../DetailPageV3ActionButtons/DetailPageV3ActionVote'
import DetailPageV3CourseJumbotron from './DetailPageV3CourseJumbotron'
import DetailPageV3CourseTabs from './DetailPageV3CourseTabs'

const DetailPageV3Course = (props) => {
  const {
    auth,
    user,
    detailData = Map(),
    staticText,
  } = props

  const ga4EventData = {
    content_name: detailData.get('title'),
    content_id: detailData.get('contentId'),
    content_type: detailData.get('contentType'),
    ui_location: 'Course Detail Page',
  }

  const jwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const titleGroupId = detailData.get('titleGroupId')
  const contentId = detailData.get('contentId')
  const assets = detailData.get('assets') || List()

  const [getCourseModulesData, { data, loading, refetch }] = useLazyQuery(GET_COURSE_MODULES, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  })

  const [featuredContent, setFeaturedContent] = useState(Map())
  const [featuredContentLoading, setFeaturedContentLoading] = useState(true)
  const [modulesData, setModulesData] = useState(List())
  const [resourcesData, setResourcesData] = useState(List())
  const [practicesData, setPracticesData] = useState(List())
  const [progressData, setProgressData] = useState(Map())

  const getFeaturedContent = useCallback(async () => {
    if (modulesData.size === 0) {
      return
    }

    const featuredEpisode = await getFeaturedEpisode({
      id: contentId,
      auth,
      userLanguage,
      courseContentId: contentId,
    })

    const nextContentId = _get(featuredEpisode, ['titles', 0, 'nid'])

    const content = getCourseFeaturedContent(modulesData, nextContentId)

    if (content.size > 0) {
      setFeaturedContent(content)
    }

    setFeaturedContentLoading(false)
  }, [contentId, auth, userLanguage, modulesData])

  useEffect(() => {
    if (titleGroupId) {
      getCourseModulesData({
        variables: {
          titleGroupId,
          language: [userLanguage, '_primary'],
          primaryLanguage: [userLanguage],
          imageType: CONTENT_IMAGE_LIST,
        },
        context: {
          jwt,
        },
      })
    }
  }, [titleGroupId, userLanguage, jwt])

  useEffect(() => {
    if (!loading && data) {
      const {
        courseModulesData,
        courseResourcesData,
        coursePracticesData,
        courseProgressData,
      } = getCourseTabsData(data)
      setModulesData(courseModulesData)
      setResourcesData(assets.concat(courseResourcesData))
      setPracticesData(coursePracticesData)
      setProgressData(courseProgressData)
    }
  }, [data, assets, loading])

  useEffect(() => {
    getFeaturedContent()
  }, [getFeaturedContent])

  const lengthMetaItem = fromJS({
    textKey: 'length',
    items: [{
      title: `${modulesData.size} ${modulesData.size > 1 ? staticText.get('steps') : staticText.get('step')}`,
    }],
    imageUrls: null,
  })

  if ((loading || featuredContentLoading) && !data) {
    return (
      <div className="detail-page-v3">
        <div className="detail-page-v3__loading">
          <Sherpa />
        </div>
      </div>
    )
  }

  return (
    <div className="detail-page-v3 detail-page-v3--course">
      <DetailPageV3CourseJumbotron
        detailData={detailData}
        modulesData={modulesData}
        ga4EventData={ga4EventData}
        progressData={progressData}
        featuredContent={featuredContent}
        onCourseStateChange={refetch}
      />
      <div className="detail-page-v3__content">
        <div className="detail-page-v3__layout-wrapper">
          <DetailPageV3Title title={detailData.get('title')} />
          <DetailPageV3ActionBar
            metaItems={
              <>
                <div className="detail-page-v3__meta-items--desktop">
                  <DetailPageV3InfoMetaItems detailData={detailData} isInline={false} />
                  <DetailPageV3MetaItem metaItem={lengthMetaItem} staticText={staticText} />
                </div>
                <div className="detail-page-v3__meta-items--mobile">
                  <DetailPageV3InfoMetaItems detailData={detailData} isInline={true} />
                  <DetailPageV3MetaItem
                    metaItem={lengthMetaItem}
                    staticText={staticText}
                    isInline={true}
                  />
                </div>
              </>
            }
            actionButtons={
              <DetailPageV3ActionButtons
                primaryButtons={
                  <>
                    <DetailPageV3ActionVote
                      detailData={detailData}
                      ga4EventData={ga4EventData}
                    />
                    <DetailPageV3ActionCopyLink
                      detailData={detailData}
                    />
                  </>
                }
              />
            }
          />
          <DetailPageV3CourseTabs
            detailData={detailData}
            modulesData={modulesData}
            resourcesData={resourcesData}
            practicesData={practicesData}
            progressData={progressData}
            featuredContent={featuredContent}
            onCourseStateChange={refetch}
          />
        </div>
      </div>
    </div>
  )
}
export default connectRedux((state) => ({
  auth: state.auth,
  user: state.user,
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3Course)
